import { useRef, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface DatePartsInputProps {
  value: string;
  onChange: (val: string) => void;
  readOnly: boolean;
  onValidityChange?: (isValid: boolean) => void;
  errorMessage?: string;
}

const DatePartsInput = ({
  value,
  onChange,
  readOnly,
  onValidityChange,
  errorMessage,
}: DatePartsInputProps) => {
  const parts = value ? value.split('/') : ['', '', ''];
  const [d, m, y] = [parts[0] || '', parts[1] || '', parts[2] || ''];
  const [touched, setTouched] = useState(false);

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const isValidDate = (dateStr: string) => {
    if (!dateStr) return true;
    return dayjs(dateStr, 'DD/MM/YY', true).isValid();
  };

  const isInvalid = !isValidDate(value);
  const showError = touched && isInvalid;

  const onValidityChangeRef = useRef(onValidityChange);

  useEffect(() => {
    onValidityChangeRef.current = onValidityChange;
  }, [onValidityChange]);

  useEffect(() => {
    onValidityChangeRef.current?.(!isInvalid);
  }, [isInvalid]);

  const handleBlur = () => {
    setTouched(true);
  };

  const handleChange = (part: 'd' | 'm' | 'y', val: string) => {
    const cleanVal = val.replace(/\D/g, '').slice(0, 2);
    let newD = d,
      newM = m,
      newY = y;
    if (part === 'd') {
      newD = cleanVal;
      if (cleanVal.length === 2) dayRef.current?.blur();
      if (cleanVal.length === 2) monthRef.current?.focus();
    }
    if (part === 'm') {
      newM = cleanVal;
      if (cleanVal.length === 2) yearRef.current?.focus();
    }
    if (part === 'y') {
      newY = cleanVal;
      if (cleanVal.length === 2) setTouched(true);
    }

    onChange(`${newD}/${newM}/${newY}`);
  };

  return (
    <div className="d-flex flex-column align-items-center">
      <div
        className={`form-control d-flex align-items-center p-0 ${
          showError ? 'is-invalid' : ''
        } ${readOnly ? 'bg-light' : 'bg-white'}`}
        style={{ width: 'fit-content', height: 'calc(1.5em + 0.75rem + 2px)' }}
      >
        <input
          ref={dayRef}
          value={d}
          onChange={(e) => handleChange('d', e.target.value)}
          onBlur={handleBlur}
          readOnly={readOnly}
          placeholder="DD"
          className="border-0 text-center bg-transparent"
          style={{ width: '2rem', outline: 'none' }}
        />
        <span className="text-muted">/</span>
        <input
          ref={monthRef}
          value={m}
          onChange={(e) => handleChange('m', e.target.value)}
          onBlur={handleBlur}
          readOnly={readOnly}
          placeholder="MM"
          className="border-0 text-center bg-transparent"
          style={{ width: '2rem', outline: 'none' }}
        />
        <span className="text-muted">/</span>
        <input
          ref={yearRef}
          value={y}
          onChange={(e) => handleChange('y', e.target.value)}
          onBlur={handleBlur}
          readOnly={readOnly}
          placeholder="AA"
          className="border-0 text-center bg-transparent"
          style={{ width: '2rem', outline: 'none' }}
        />
      </div>
      {showError && errorMessage && (
        <small className="text-danger mt-1">{errorMessage}</small>
      )}
    </div>
  );
};

export default DatePartsInput;
